import { useState } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { APIError, ApiErrors } from '@interxfi/api-client/api-error';
import { useLogin } from '@interxfi/api-client/userauth/login';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Typography } from '@interxfi/design-system/components/typography';
import { decodeFromBase64 } from '@interxfi/utilities/encoding/base64';
import { AppPasswordInputWithLens } from '../../../../components/form/app-password-input';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import { AuthLayout } from '../../../../features/auth/layout';
import { usePendingCredentials } from '../../../../features/auth/login/pending-credentials-context';
import { useGetDefaultAuthenticatedRoute } from '../../../../util/auth';
import { useErrorHandler } from '../../../../util/error-handler';

const loginSearchSchema = z.object({
  redirectTo: z.string().optional(),
});

export const Route = createFileRoute('/_auth/login/_login/')({
  component: Login,
  validateSearch: loginSearchSchema,
});

function loginErrorMessageGetter(error: Error | null): string | undefined {
  if (!error) {
    return undefined;
  }

  if (error instanceof APIError) {
    if (error.message === ApiErrors.MissingOTP) {
      return undefined;
    }

    if ([403, 401].includes(error.status)) {
      return "Oops, we didn't recognize that email and password combination.";
    }

    if (error.status === 503) {
      return 'Service temporarily unavailable, please try your request again later.';
    }
  }

  return error.message;
}

const loginFormSchema = z.object({
  email: z.email({ error: 'Email must be a valid email' }).nonempty({ error: 'Email is required' }),
  password: z.string().nonempty({ error: 'Password is required' }),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

const defaultValues: LoginFormValues = {
  email: '',
  password: '',
};

function Login() {
  const getRedirectTo = useGetDefaultAuthenticatedRoute();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = Route.useNavigate();
  const { redirectTo: redirectToAfterAuthenticated } = Route.useSearch();
  const { set: setPendingCredentials } = usePendingCredentials();
  const { mutateAsync, isPending, error } = useLogin();
  useErrorHandler(error, loginErrorMessageGetter);
  const [buttonState, buttonRef] = useButtonState(isPending, loginErrorMessageGetter(error));
  const form = useForm({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(loginFormSchema),
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = form;
  const lens = useLens({ control });

  async function onSubmit({ email, password }: LoginFormValues) {
    try {
      setIsSubmitting(true);
      await mutateAsync({ credentials: { basicAuth: { email, password } } });
      await navigate({
        to: redirectToAfterAuthenticated ? decodeFromBase64(redirectToAfterAuthenticated) : await getRedirectTo(),
      });
    } catch (err) {
      if (err instanceof APIError) {
        if (err.message === ApiErrors.MissingOTP) {
          setPendingCredentials({ email, password });
          await navigate({ to: '/login/mfa', search: redirectToAfterAuthenticated ? { redirectTo: redirectToAfterAuthenticated } : undefined });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <FormProvider {...form}>
      <AuthLayout>
        <HorizontalStack align="space-between" blockAlign="end">
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading" weight="bold">
            Sign In
          </Typography>
          <TextLink LinkElement={Link} size="medium" to="/request-access" underline="hover">
            Request Access
          </TextLink>
        </HorizontalStack>

        <VerticalStack as="form" gap={4} onSubmit={handleSubmit(onSubmit)}>
          <AppTextInputWithLens label="Email" lens={lens.focus('email')} required />
          <AppPasswordInputWithLens label="Password" lens={lens.focus('password')} required />

          <Button disabled={!isValid || isSubmitting} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
            Sign In
          </Button>
        </VerticalStack>

        <VerticalStack inlineAlign="center">
          <TextLink LinkElement={Link} size="medium" to="/forgot-password" underline="hover">
            Forgot Password
          </TextLink>
        </VerticalStack>
      </AuthLayout>
    </FormProvider>
  );
}
